import i18n from '../translations/i18n';
import {
  UserDTO,
  PlantDTO,
  SceneDTO,
  DeviceDTO,
  IDeviceDTO,
  ServerModel,
  RoomObjectDTO,
  PlantDetailDTO,
  PlantSharingDTO,
  PlantMxServerDTO,
  ExtDevice,
  ExtComponentType,
  PlayerExtDevice,
  PlayerSystem,
  ColoredLightExtDevice,
  Slot,
  SlotStatus,
  CommandIcon,
  PlantIconEnum,
  ThermoTypeOfObject,
  ThermoAssociatedPump,
  RoomType,
  LightType,
  SceneType,
  RoomObjectType,
  SceneObjectType,
  ThermoActuatorType,
  AggregateCommandType,
  ColoredLightType,
  XmeterExtDevice,
  PlantType,
  Language,
  EntrancePlantDetailDTO,
  ApartmentInvitationDTO,
  EpUserRole,
  EnergyMeterAndLoadControlType
} from '@/../client-generator/generated-clients/plants';
import {
  ComponentType,
  IDefaultDevicesSettingsDTO,
  RoomObject,
} from '@/../client-generator/generated-clients/device-settings';
import { SlotRuleServiceType } from '../services/slots-assign-rules-service/slots-assign-rules-interfaces';

const $t = i18n.global.t;

const cloneObject = <T>(object: T): T => {
  return JSON.parse(JSON.stringify(object));
};

const getUserInitials = (usersName: string) => {
  const names = usersName.split(' ');

  if (names.length === 1) {
    return names[0][0];
  }

  return `${names[0][0]}${names[1][0]}`;
};

const roomObjectTypeIconMapping: Record<RoomObjectType, string> = {
  [RoomObjectType.None]: 'icon-none',
  [RoomObjectType.Lock]: 'icon-lock',
  [RoomObjectType.Light]: 'icon-light',
  [RoomObjectType.Blind]: 'icon-blind',
  [RoomObjectType.Alarm]: 'icon-security',
  [RoomObjectType.Xmeter]: 'icon-xmeter',
  [RoomObjectType.Socket]: 'icon-socket',
  [RoomObjectType.Player]: 'icon-player',
  [RoomObjectType.Shutter]: 'icon-shutter',
  [RoomObjectType.Fancoil]: 'icon-fancoil',
  [RoomObjectType.WirelessCommand]: 'icon-wifi',
  [RoomObjectType.Thermostat]: 'icon-thermostat',
  [RoomObjectType.Thermostat99zones]: 'icon-thermostat',
  [RoomObjectType.TowelWarmer]: 'icon-towel-rail',
  [RoomObjectType.ColoredLight]: 'icon-colored-light',
  [RoomObjectType.AlarmSensor]: 'icon-security',
  [RoomObjectType.DimmerDmx]: 'icon-dimmer-dmx',
  [RoomObjectType.ScheduledScenarioPlus]: 'icon-general-object',
  [RoomObjectType.CentralLoadControl]: 'icon-load-control',
  [RoomObjectType.DimmerDali]: 'icon-dimmer-dmx',
  [RoomObjectType.StaircaseLight]: 'icon-star',
  [RoomObjectType.FloorCall]: 'icon-floor-call',

  [RoomObjectType.ActuactorLoadControl]: 'icon-general-object',
  [RoomObjectType.Contact]: 'icon-general-object',
  [RoomObjectType.Actuactor]: 'icon-general-object',
  [RoomObjectType.VdeLock]: 'icon-general-object',
  [RoomObjectType.Unknown]: 'icon-general-object',
};

const roomObjectTypeLabelMapping: Record<RoomObjectType, string> = {
  [RoomObjectType.None]: '',
  [RoomObjectType.Lock]: $t('AppResource.Lock'),
  [RoomObjectType.Light]: $t('AppResource.Light'),
  [RoomObjectType.Blind]: $t('AppResource.Blind'),
  [RoomObjectType.Alarm]: $t('AppResource.Alarm'),
  [RoomObjectType.Xmeter]: $t('AppResource.Xmeter'),
  [RoomObjectType.Socket]: $t('AppResource.Socket'),
  [RoomObjectType.Player]: $t('AppResource.Player'),
  [RoomObjectType.Shutter]: $t('AppResource.Shutter'),
  [RoomObjectType.Fancoil]: $t('AppResource.Fancoil'),
  [RoomObjectType.WirelessCommand]: $t('AppResource.WirelessCommand'),
  [RoomObjectType.Thermostat]: $t('AppResource.Thermostat'),
  [RoomObjectType.Thermostat99zones]: $t('AppResource.Thermostat99zones'),
  [RoomObjectType.TowelWarmer]: $t('AppResource.TowelWarmer'),
  [RoomObjectType.ColoredLight]: $t('AppResource.ColoredLight'),
  [RoomObjectType.AlarmSensor]: $t('AppResource.AlarmSensor'),
  [RoomObjectType.DimmerDmx]: $t('AppResource.DimmerDmx'),
  [RoomObjectType.ScheduledScenarioPlus]: $t('AppResource.Scene'),
  [RoomObjectType.Contact]: $t('AppResource.Contact'),
  [RoomObjectType.DimmerDali]: $t('AppResource.DimmerDali'),
  [RoomObjectType.Actuactor]: $t('AppResource.Actuactor'),
  [RoomObjectType.CentralLoadControl]: $t('AppResource.CentralLoadControl'),
  [RoomObjectType.ActuactorLoadControl]: $t('ControlledLoad'),
  [RoomObjectType.VdeLock]: $t('AppResource.Lock'),
  [RoomObjectType.StaircaseLight]: $t('AppResource.StaircaseLight'),
  [RoomObjectType.FloorCall]: $t('AppResource.FloorCall'),
  [RoomObjectType.Unknown]: '',
};

const extDeviceTypeIconMapping: Record<ExtComponentType, string> = {
  [ExtComponentType.Player]: 'icon-music-player',
  [ExtComponentType.ColoredLight]: 'icon-network',
  [ExtComponentType.Xmeter]: 'icon-network',
  [ExtComponentType.AlarmSensor]: 'icon-security',
  [ExtComponentType.AlarmCentralUnit]: 'icon-security',
  [ExtComponentType.DimmerDmx]: 'icon-dimmer-dmx',
};

const sceneObjectTypeIconMapping: Record<SceneObjectType, string> = {
  [SceneObjectType.None]: 'icon-general-object',
  [SceneObjectType.Lock]: 'icon-lock',
  [SceneObjectType.Light]: 'icon-light',
  [SceneObjectType.Blind]: 'icon-blind',
  [SceneObjectType.Email]: 'icon-email',
  [SceneObjectType.Dimmer]: 'icon-light',
  [SceneObjectType.Xmeter]: 'icon-xmeter',
  [SceneObjectType.Player]: 'icon-player',
  [SceneObjectType.Socket]: 'icon-socket',
  [SceneObjectType.Fancoil]: 'icon-fancoil',
  [SceneObjectType.Shutter]: 'icon-shutter',
  [SceneObjectType.Command]: 'icon-command',
  [SceneObjectType.Weather]: 'icon-weather',
  [SceneObjectType.DateTime]: 'icon-datetime',
  [SceneObjectType.Thermostat]: 'icon-thermostat',
  [SceneObjectType.TowelWarmer]: 'icon-towel-rail',
  [SceneObjectType.GroupOfShutters]: 'icon-shutter',
  [SceneObjectType.GroupOfLights]: 'icon-group-lights',
  [SceneObjectType.ColoredLights]: 'icon-colored-light',
  [SceneObjectType.GeneralCommandsLights]: 'icon-light',
  [SceneObjectType.GeneralCommandsShutters]: 'icon-shutter',
  [SceneObjectType.PushNotificationAction]: 'icon-push-notification',
  [SceneObjectType.Alarm]: 'icon-security',
  [SceneObjectType.AlarmSensor]: 'icon-security',
  [SceneObjectType.DimmerDmx]: 'icon-dimmer-dmx',
  [SceneObjectType.Dali]: 'icon-dimmer-dmx',

  [SceneObjectType.ActuactorLoadControl]: 'icon-general-object',
};

const productAndObjectTypeIconMapping: Record<EnergyMeterAndLoadControlType, string> = {
  [EnergyMeterAndLoadControlType.Socket]: 'icon-energy',
  [EnergyMeterAndLoadControlType.Light]: 'icon-energy-light-socket',
  [EnergyMeterAndLoadControlType.Fridge]: 'icon-energy-fridge',
  [EnergyMeterAndLoadControlType.Oven]: 'icon-energy-oven',
  [EnergyMeterAndLoadControlType.Washingmachine]: 'icon-energy-washing',
  [EnergyMeterAndLoadControlType.Dryer]: 'icon-energy-dryer',
  [EnergyMeterAndLoadControlType.Dishwasher]: 'icon-energy-dishwasher',
  [EnergyMeterAndLoadControlType.Multimedia]: 'icon-energy-multimedia',
  [EnergyMeterAndLoadControlType.Internetbox]: 'icon-energy-internet-box',

  [EnergyMeterAndLoadControlType.None]: 'icon-general-object',
  [EnergyMeterAndLoadControlType.Kitchenappliances]: 'icon-general-object',
  [EnergyMeterAndLoadControlType.Shutter]: 'icon-general-object',
  [EnergyMeterAndLoadControlType.Heating]: 'icon-general-object',
  [EnergyMeterAndLoadControlType.Boiler]: 'icon-general-object',
  [EnergyMeterAndLoadControlType.Airconditioner]: 'icon-general-object',
  [EnergyMeterAndLoadControlType.Other]: 'icon-general-object'
};

export interface ObjectTypes {
  name: string;
  icon: string;
  type: RoomObjectType;
}

const objectTypes: ObjectTypes[] = [
  {
    name: $t('AppResource.Light'),
    icon: roomObjectTypeIconMapping[RoomObjectType.Light],
    type: RoomObjectType.Light,
  },
  {
    name: $t('AppResource.ColoredLight'),
    icon: roomObjectTypeIconMapping[RoomObjectType.ColoredLight],
    type: RoomObjectType.ColoredLight,
  },
  {
    name: $t('AppResource.DimmerDmx'),
    icon: roomObjectTypeIconMapping[RoomObjectType.DimmerDmx],
    type: RoomObjectType.DimmerDmx,
  },
  {
    name: $t('AppResource.Shutter'),
    icon: roomObjectTypeIconMapping[RoomObjectType.Shutter],
    type: RoomObjectType.Shutter,
  },
  {
    name: $t('AppResource.Socket'),
    icon: roomObjectTypeIconMapping[RoomObjectType.Socket],
    type: RoomObjectType.Socket,
  },
  {
    name: $t('AppResource.Lock'),
    icon: roomObjectTypeIconMapping[RoomObjectType.Lock],
    type: RoomObjectType.Lock,
  },
  {
    name: $t('AppResource.Thermostat'),
    icon: roomObjectTypeIconMapping[RoomObjectType.Thermostat],
    type: RoomObjectType.Thermostat,
  },
  {
    name: $t('AppResource.Thermostat'),
    icon: roomObjectTypeIconMapping[RoomObjectType.Thermostat99zones],
    type: RoomObjectType.Thermostat99zones,
  },
  {
    name: $t('AppResource.Fancoil'),
    icon: roomObjectTypeIconMapping[RoomObjectType.Fancoil],
    type: RoomObjectType.Fancoil,
  },
  {
    name: $t('AppResource.TowelWarmer'),
    icon: roomObjectTypeIconMapping[RoomObjectType.TowelWarmer],
    type: RoomObjectType.TowelWarmer,
  },
  {
    name: 'Wireless',
    icon: roomObjectTypeIconMapping[RoomObjectType.WirelessCommand],
    type: RoomObjectType.WirelessCommand,
  },
  {
    name: $t('AppResource.Blind'),
    icon: roomObjectTypeIconMapping[RoomObjectType.Blind],
    type: RoomObjectType.Blind,
  },
  {
    name: $t('AppResource.Xmeter'),
    icon: roomObjectTypeIconMapping[RoomObjectType.Xmeter],
    type: RoomObjectType.Xmeter,
  },
  {
    name: $t('AppResource.Player'),
    icon: roomObjectTypeIconMapping[RoomObjectType.Player],
    type: RoomObjectType.Player,
  },
  {
    name: $t('alarm.alarmsensor'),
    icon: roomObjectTypeIconMapping[RoomObjectType.AlarmSensor],
    type: RoomObjectType.AlarmSensor,
  },
  {
    name: $t('AppResource.CentralLoadControl'),
    icon: roomObjectTypeIconMapping[RoomObjectType.CentralLoadControl],
    type: RoomObjectType.CentralLoadControl,
  },
  {
    name: $t('ControlledLoad'),
    icon: roomObjectTypeIconMapping[RoomObjectType.ActuactorLoadControl],
    type: RoomObjectType.ActuactorLoadControl,
  },
  {
    name: $t('AppResource.DimmerDali'),
    icon: roomObjectTypeIconMapping[RoomObjectType.DimmerDali],
    type: RoomObjectType.DimmerDali,
  },
  {
    name: $t('AppResource.StaircaseLight'),
    icon: roomObjectTypeIconMapping[RoomObjectType.StaircaseLight],
    type: RoomObjectType.StaircaseLight,
  },
  {
    name: $t('AppResource.FloorCall'),
    icon: roomObjectTypeIconMapping[RoomObjectType.FloorCall],
    type: RoomObjectType.FloorCall,
  },
  {
    name: $t('AppResource.Alarm'),
    icon: roomObjectTypeIconMapping[RoomObjectType.Alarm],
    type: RoomObjectType.Alarm,
  },
  {
    name: $t('AppResource.Lock'),
    icon: roomObjectTypeIconMapping[RoomObjectType.VdeLock],
    type: RoomObjectType.VdeLock,
  },
];

const defaultNewPlant = new PlantDTO({
  tags: [],
  name: '',
  address: '',
  country: 'IT',
  roomsCounts: [],
  icon: PlantIconEnum.ICON_1,
  mxServer: new PlantMxServerDTO({ model: undefined }),
  plantType: PlantType.AUTOMATION_PLANT,
});

const defaultNewEntrancePlant = new EntrancePlantDetailDTO({
  tags: [],
  name: '',
  address: '',
  country: 'IT',
  icon: PlantIconEnum.ICON_1,
  plantType: PlantType.ENTRANCE_PLANT,
});

const getRoomIconFromType = (roomType: RoomType) => {
  let iconName = '';

  switch (roomType) {
    case RoomType.Livingroom:
      iconName = 'icon-livingroom';
      break;
    case RoomType.Bathroom:
      iconName = 'icon-bathroom';
      break;
    case RoomType.Kitchen:
      iconName = 'icon-kitchen';
      break;
    case RoomType.Bedroom:
      iconName = 'icon-bedroom';
      break;
    case RoomType.Lobby:
      iconName = 'icon-entrance';
      break;
    case RoomType.Corridor:
      iconName = 'icon-corridor';
      break;
    case RoomType.Garage:
      iconName = 'icon-garage';
      break;
    case RoomType.Outdoor:
      iconName = 'icon-outdoor';
      break;
    case RoomType.Stairs:
      iconName = 'icon-staircase';
      break;
    case RoomType.Dining_room:
      iconName = 'icon-diningroom';
      break;
    case RoomType.Toilets:
      iconName = 'icon-toilets';
      break;
    case RoomType.Home_office:
      iconName = 'icon-office';
      break;
    case RoomType.Custom:
      iconName = 'icon-blueprint';
      break;
    case RoomType.Electrical_cabinet:
      iconName = 'icon-electrical-cabinet';
      break;
  }

  return iconName;
};

const getObjectIconFromType = (objectType: RoomObjectType) => {
  return roomObjectTypeIconMapping[objectType];
};

const getObjectLabelFromType = (objectType: RoomObjectType) => {
  return roomObjectTypeLabelMapping[objectType];
};

const getExtDeviceIconFromType = (extDeviceType: ExtComponentType) => {
  return extDeviceTypeIconMapping[extDeviceType];
};

const getIconFromProductType = (productType?: EnergyMeterAndLoadControlType) => {
  if (!productType) return 'icon-general-object';
  return productAndObjectTypeIconMapping[productType];
};

const getObjectDescLabel = (object: RoomObjectDTO) => {
  let label = '';

  switch (object.type) {
    case RoomObjectType.ColoredLight:
      label = $t('AppResource.ColoredLight');
      break;
    case RoomObjectType.Player:
      label = $t('AppResource.MusicPlayer');
      break;
    case RoomObjectType.Xmeter:
      label = $t('AppResource.Xmeter');
      break;
    case RoomObjectType.Lock:
      label = $t('AppResource.Lock');
      break;
    case RoomObjectType.Shutter:
      label = $t('AppResource.ShutterObject');
      break;
    case RoomObjectType.Blind:
      label = $t('AppResource.Blind');
      break;
    case RoomObjectType.Socket:
      label = $t('AppResource.ControlledSocket');
      break;
    case RoomObjectType.Fancoil:
      label = `${
        object.thermoTypeOfObject == ThermoTypeOfObject.Heating
          ? $t('AppResource.Heating')
          : $t('AppResource.Cooling')
      } ${$t('AppResource.FancoilLower')}`;
      break;
    case RoomObjectType.TowelWarmer:
      label = $t('AppResource.TowelWarmer');
      break;
    case RoomObjectType.WirelessCommand:
      label = '';
      break;
    case RoomObjectType.Thermostat:
      if (object.thermoTypeOfObject === ThermoTypeOfObject.Cooling)
        label = $t('AppResource.Cooling');
      else if (object.thermoTypeOfObject === ThermoTypeOfObject.Heating)
        label = $t('AppResource.Heating');
      else if (
        object.thermoTypeOfObject === ThermoTypeOfObject.HeatingAndCooling
      )
        label = $t('AppResource.HeatingAndCooling');
      if (object.thermoAssociatedPump === ThermoAssociatedPump.Yes)
        label += ' plant with associated pump';
      else label += ' plant without associated pump';
      break;
    case RoomObjectType.Light:
      label = `${
        object.lightType === LightType.Dimmer
          ? $t('AppResource.Dimmer')
          : $t('AppResource.OnOffLight')
      } ${$t('AppResource.Object')}`;
      break;
    case RoomObjectType.DimmerDmx:
      label = $t('AppResource.DimmerDmx');
      break;
    case RoomObjectType.CentralLoadControl:
      label = $t('AppResource.CentralLoadControl');
      break;
    case RoomObjectType.ActuactorLoadControl:
      label = $t('ControlledLoad');
      break;
  }

  return label;
};

const getDeviceIconName = (commandIcon?: CommandIcon) => {
  if (!commandIcon) return '';
  return ` | icon ${CommandIcon[commandIcon]}`;
};

const getThermo99Description = (roomObject: RoomObject) => {
  return `${roomObject.thermo99SelectedZone} - ${$t(
    `AppResource.${
      roomObject.thermo99HasFancoil ? 'WithLower' : 'WithoutLower'
    }`
  )} ${$t('AppResource.FancoilLower')}`;
};

const getPlantIconImages = () => {
  return [
    {
      imageName: 'house-3',
      value: PlantIconEnum.ICON_1,
    },
    {
      imageName: 'house-2',
      value: PlantIconEnum.ICON_2,
    },
    {
      imageName: 'house-1',
      value: PlantIconEnum.ICON_3,
    },
    {
      imageName: 'apt-1',
      value: PlantIconEnum.ICON_4,
    },
    {
      imageName: 'apt-2',
      value: PlantIconEnum.ICON_5,
    },
  ];
};

const getEpPlantIconImages = () => {
  return [
    {
      imageName: 'entrance_plant_1',
      value: PlantIconEnum.ICON_1,
    },
    {
      imageName: 'entrance_plant_2',
      value: PlantIconEnum.ICON_2,
    },
    {
      imageName: 'entrance_plant_3',
      value: PlantIconEnum.ICON_3,
    },
    {
      imageName: 'entrance_plant_4',
      value: PlantIconEnum.ICON_4,
    },
    {
      imageName: 'entrance_plant_5',
      value: PlantIconEnum.ICON_5,
    },
  ];
};

const getPlantIconImageUrls = ({ imageName }: { imageName: string }) => {
  return [
    require(`@/assets/images/plants/${imageName}.jpg`),
    require(`@/assets/images/plants/${imageName}@2x.jpg`),
    require(`@/assets/images/plants/${imageName}@3x.jpg`),
  ];
};

const getEpPlantIconImageUrls = ({ imageName }: { imageName: string }) => {
  return [
    require(`@/assets/images/plants/${imageName}.jpg`),
    require(`@/assets/images/plants/${imageName}@2x.jpg`),
    require(`@/assets/images/plants/${imageName}@3x.jpg`),
  ];
};

const getPlantIconImageUrlsByType = (
  plantTypeIcon?: PlantIconEnum,
  plantCustomIconUrl?: string,
  plantType?: PlantType
) => {
  if (plantTypeIcon == PlantIconEnum.ICON_CUSTOM) return [plantCustomIconUrl];
  if (plantType == PlantType.ENTRANCE_PLANT) {
    const image = getEpPlantIconImages().find((x) => x.value === plantTypeIcon);
    return image ? getEpPlantIconImageUrls(image) : [];
  }
  const image = getPlantIconImages().find((x) => x.value === plantTypeIcon);
  return image ? getPlantIconImageUrls(image) : [];
};

const getDeviceIcon = (device: IDeviceDTO) => {
  let iconName = '';

  if (device.selectedComponentType === ComponentType.Command)
    iconName = 'icon-command';
  else iconName = 'icon-network';
  return iconName;
};

const getExtDeviceIdentifier = (extDevice: ExtDevice) => {
  switch (extDevice.componentType) {
    case ExtComponentType.Player: {
      const player = castToPlayerExtDevice(extDevice);
      return `${
        player.playerSystem == PlayerSystem.Nuvo ? 'NuVo Zone' : 'Sonos'
      } ${player.macAddress}`;
    }
    case ExtComponentType.ColoredLight: {
      const coloredLight = castToColoredLightExtDevice(extDevice);
      switch (coloredLight.coloredLightType) {
        case ColoredLightType.Dmx:
          return $t('AppResource.DmxEnttecOde');
        case ColoredLightType.Lifx:
          return $t('AppResource.Lifx');
        case ColoredLightType.PhilipsHue:
          return $t('AppResource.PhilipsHue');
      }
      break;
    }
    case ExtComponentType.Xmeter: {
      const xmDevice = castToXmeterExtDevice(extDevice);
      return `${$t('AppResource.Address')} ${xmDevice.address}`;
    }
  }
};

const castToColoredLightExtDevice = (extDevice: ExtDevice): ColoredLightExtDevice => {
  const configuration = extDevice.deviceConfiguration;
  if (!configuration) return new ColoredLightExtDevice();

  const codes = {
    COLORED_LIGHT_TYPE_CODE: "0",
    PHILIPS_HUE_BRIDGE_CODE: "11",
    PHILIPS_HUE_LAMP_CODE: "12",
    PHILIPS_HUE_LAMP_NAME_CODE: "13",
    LIFX_LAMP_CODE: "21",
    DMX_MAC_ADDRESS_CODE: "31",
    DMX_NODE_CODE: "32",
  };

  return new ColoredLightExtDevice({
    coloredLightType:
      ColoredLightType[
        configuration[codes.COLORED_LIGHT_TYPE_CODE] as keyof typeof ColoredLightType
      ],
    philipsHueBridge: configuration[codes.PHILIPS_HUE_BRIDGE_CODE] ?? undefined,
    philipsHueLamp: configuration[codes.PHILIPS_HUE_LAMP_CODE] ?? undefined,
    philipsHueLampName: configuration[codes.PHILIPS_HUE_LAMP_NAME_CODE] ?? undefined,
    lifxLamp: configuration[codes.LIFX_LAMP_CODE] ?? undefined,
    dmxMacAddress: configuration[codes.DMX_MAC_ADDRESS_CODE] ?? undefined,
    dmxNode: configuration[codes.DMX_NODE_CODE] ?? undefined,
  });
};

const castToXmeterExtDevice = (extDevice: ExtDevice): XmeterExtDevice => {
  const configuration = extDevice.deviceConfiguration;
  if (!configuration) return new XmeterExtDevice();

  const codes = {
    ADDRESS_CODE: "1",
  };

  return new XmeterExtDevice({
    address: configuration[codes.ADDRESS_CODE] ?? undefined,
  });
}

const castToPlayerExtDevice = (extDevice: ExtDevice): PlayerExtDevice => {
  const configuration = extDevice.deviceConfiguration;
  if (!configuration) return new PlayerExtDevice();

  const codes = {
    PLAYER_SYSTEM_CODE: "0",
    MAC_ADDRESS_CODE: "1",
    PLAYER_NAME_CODE: "2",
  };

  return new PlayerExtDevice({
    playerSystem:
      PlayerSystem[
        configuration[codes.PLAYER_SYSTEM_CODE] as keyof typeof PlayerSystem
      ],
    macAddress: configuration[codes.MAC_ADDRESS_CODE] ?? undefined,
    name: configuration[codes.PLAYER_NAME_CODE] ?? undefined,
  });
};

const getExtDeviceActiveLink = (extDevice: ExtDevice) => {
  if (extDevice.componentType == ExtComponentType.ColoredLight) {
    const coloredLight = extDevice as ColoredLightExtDevice;
    switch (coloredLight.coloredLightType) {
      case ColoredLightType.PhilipsHue:
        return `Bridge ${coloredLight.philipsHueBridge} - ${coloredLight.philipsHueLampName}`;
      case ColoredLightType.Lifx:
        return `Ampoule Lifx ${coloredLight.lifxLamp}`;
      case ColoredLightType.Dmx:
        return `DMX ${coloredLight.dmxMacAddress} - Node ${coloredLight.dmxNode}`;
    }
  } else {
    return '';
  }
};

const getDeviceIconFromSettings = (device: IDefaultDevicesSettingsDTO) => {
  let iconName = '';
  if (!device.isAggregateRoomObject) {
    if (device.selectedComponentType == ComponentType.Command)
      iconName = 'icon-command';
    else iconName = 'icon-network';
  } else {
    iconName = getRoomIconFromType(device.roomType ?? RoomType.Custom);
  }
  return iconName;
};

const getConfiguredChannelsOrModulesSettings = (device: DeviceDTO) => {
  if (!device) return;
  return device.defaultSettings?.find(
    (x) => x.selectedComponentType == device.selectedComponentType
  );
};

const getPlantLastModifyByIcon = (lastModifyByDesktop: boolean | undefined) => {
  if (lastModifyByDesktop) return 'icon-desktop';
  return 'icon-mobile-device';
};

const getPlantLastModifyByLabel = (
  lastModifyByDesktop: boolean | undefined
) => {
  if (lastModifyByDesktop) return $t('general.desktop');
  return $t('general.mobile');
};

const getUserInitialsForBubble = (
  user?: PlantSharingDTO | UserDTO | ApartmentInvitationDTO | EpUserRole
) => {
  if (!user) return '- -';
  let initials = '';
  const email =
    user.email ?? (() => ('invitedEmail' in user ? user.invitedEmail : ''))();
  if (email) {
    const emailParts = email.split('@');
    const firstEmailPart = emailParts[0];
    const usernameParts = firstEmailPart.split('.');
    if (usernameParts.length > 1)
      initials = `${usernameParts[0][0]}${usernameParts[1][0]}`;
    else initials = `${emailParts[0][0]}${emailParts[0][1]}`;
  } else {
    initials = '- -';
  }
  return initials.toUpperCase();
};

const getSlotClass = (slot: Slot) => {
  let className = '';

  switch (slot.status) {
    case SlotStatus.Configured:
      className = 'configured';
      break;
    case SlotStatus.NotConfigured:
      className = 'not-configured';
      break;
    case SlotStatus.NotUsed:
      className = 'not-used';
      break;
  }

  return className;
};

const secondsToFormattedTime = (seconds: number) => {
  const theDate = new Date(0);
  theDate.setSeconds(seconds);

  const secondsToFormattedTime =
    theDate.getUTCHours().toString().padStart(2, '0') +
    ':' +
    theDate.getUTCMinutes().toString().padStart(2, '0') +
    ':' +
    theDate.getUTCSeconds().toString().padStart(2, '0');

  return secondsToFormattedTime;
};

const formattedTimeToSeconds = (formattedTime: string) => {
  const [hours, minutes, seconds] = formattedTime.split(':');

  const secondsFinal =
    parseInt(hours, 10) * 3600 +
    parseInt(minutes, 10) * 60 +
    parseInt(seconds, 10);

  return secondsFinal;
};

const getCurrentThermoActuatorType = (
  thermoActuatorTypes: { [key: string]: ThermoActuatorType } | undefined,
  roomObjectId: string | undefined
): ThermoActuatorType => {
  let tmp =
    thermoActuatorTypes && roomObjectId
      ? thermoActuatorTypes[roomObjectId]
      : undefined;
  return tmp ?? ThermoActuatorType.None;
};

const convertThermoRuleServiceTypeToThermoActuatorType = (
  ruleServiceType: SlotRuleServiceType,
  logMappingError: boolean = false
) => {
  switch (ruleServiceType) {
    case SlotRuleServiceType.THERMO_HEATING_ACTUATORS:
      return ThermoActuatorType.HeatingValve;
    case SlotRuleServiceType.THERMO_COOLING_ACTUATORS:
      return ThermoActuatorType.CoolingValve;
    case SlotRuleServiceType.THERMO_HEATING_PUMPS:
      return ThermoActuatorType.HeatingPump;
    case SlotRuleServiceType.THERMO_COOLING_PUMPS:
      return ThermoActuatorType.CoolingPump;
    case SlotRuleServiceType.THERMO_SHARED_ACTUATORS:
      return ThermoActuatorType.SharedValve;
    case SlotRuleServiceType.THERMO_SHARED_PUMPS:
      return ThermoActuatorType.SharedPump;
    default:
      if (logMappingError)
        console.error('TO DEFINE MAPPING of current assign actuator step type');
      return ThermoActuatorType.None;
  }
};

const defaultSceneTypes = [
  SceneType.Wake_Up,
  SceneType.BedTime,
  SceneType.Home,
  SceneType.Away,
];

const isDefaultScene = (s: SceneDTO | null) => {
  if (!s) return false;
  return defaultSceneTypes.includes(s.type as SceneType);
};

const getGeneralGroupIconFromType = (
  groupType: AggregateCommandType,
  roomObjectType: RoomObjectType
) => {
  switch (roomObjectType) {
    case RoomObjectType.Light:
      return groupType === AggregateCommandType.General
        ? roomObjectTypeIconMapping[RoomObjectType.Light]
        : sceneObjectTypeIconMapping[SceneObjectType.GroupOfLights];

    case RoomObjectType.Shutter:
      return roomObjectTypeIconMapping[RoomObjectType.Shutter];
  }
};

const getSceneObjectIconFromType = (sceneObjectType?: SceneObjectType, energyObjectType?: EnergyMeterAndLoadControlType) => {
  if (sceneObjectType === undefined) return 'icon-general-object';
  if (sceneObjectType === SceneObjectType.ActuactorLoadControl && energyObjectType !== undefined) {
    return productAndObjectTypeIconMapping[energyObjectType];
  }
  return sceneObjectTypeIconMapping[sceneObjectType];
};

const isValidEnum = (value: any) => {
  return value !== null && value !== undefined && !isNaN(Number(value));
};

async function sleep(timeMs: number): Promise<void> {
  return new Promise((r) => setTimeout(() => r(), timeMs));
}

function getRandomInt(min: number, max: number, returnString = false) {
  min = Math.ceil(min);
  max = Math.floor(max);
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

const NAME_VALIDATION_DEBOUNCE_TIME = 500;

function resolveObjectPath(obj: any, path: string) {
  return path.split('.').reduce(function (prev, curr) {
    return prev ? prev[curr] : null;
  }, obj || self);
}

const hasDeviceSettingsButtonVisible = (setting: IDefaultDevicesSettingsDTO) =>
  setting?.shuttersActuatorSettings != null ||
  setting?.shuttersCommandSettings != null ||
  setting?.lightsActuatorSettings != null ||
  setting?.lightsCommandSettings != null ||
  setting?.lockCommandSettings != null ||
  setting?.lockActuatorSettings != null ||
  setting?.thermoProbeSettings != null;

const hasDeviceCommandIconButtonVisible = (
  setting: IDefaultDevicesSettingsDTO
) => setting?.hasChangeIconButtonVisible;

const parseShutterActuatorSettings = (
  defaultSettings: IDefaultDevicesSettingsDTO
) => {
  if (!defaultSettings.shuttersActuatorSettings) return;
  defaultSettings.shuttersActuatorSettings.stopPulseDuration =
    defaultSettings.shuttersActuatorSettings.stopPulseDuration! * 100;
  defaultSettings.shuttersActuatorSettings.upDownPulseDuration =
    defaultSettings.shuttersActuatorSettings.upDownPulseDuration! * 100;
  defaultSettings.shuttersActuatorSettings.presetValue =
    defaultSettings.shuttersActuatorSettings.presetValue! * 10;
  defaultSettings.shuttersActuatorSettings.delayBetweenDoorsTime =
    defaultSettings.shuttersActuatorSettings.delayBetweenDoorsTime! * 1000;
  defaultSettings.shuttersActuatorSettings.stopTime = getStopTime(
    defaultSettings.shuttersActuatorSettings.stopTime!
  );
};

const getStopTime = (initialStopTime: number) => {
  let quotient = Math.floor(initialStopTime / 60);
  let remainder = initialStopTime % 60;

  if (quotient > 0) {
    if (remainder == 0) {
      return 60000;
    } else {
      return remainder * 60000;
    }
  } else {
    return remainder * 1000;
  }
};

const getMxServerSettingsLabel = (currentPlant?: PlantDetailDTO) => {
  switch (currentPlant?.mxServer?.model) {
    case ServerModel.Classe300EOS:
      return $t('AppResource.ServerAndSettings');
    case ServerModel.MyHomeServer1:
      return $t('AppResource.ServerAndSettingsMHS1');
    case ServerModel.F460:
      return $t('AppResource.ServerAndSettingsf460');
    case ServerModel.F461:
      return $t('AppResource.ServerAndSettingsf461');
  }
};

const validLanguages = Object.keys(Language).reduce((t: any, k: any) => {
  if (!isNaN(+k)) {
    t.push({
      name: $t('AppResource.'+Language[k]),
      code: +k,
    });
  }
  return t;
}, []);

function deepEquals(a: any, b: any) {
  if (a === b) return true;

  if (a && b && typeof a == 'object' && typeof b == 'object') {
      let arrA = Array.isArray(a);
      let arrB = Array.isArray(b);
      let i;
      let length;
      let key;

      if (arrA && arrB) {
          length = a.length;
          if (length != b.length) return false;
          for (i = length; i-- !== 0;)
            if (!deepEquals(a[i], b[i])) return false;
          return true;
      }

      if (arrA != arrB) return false;

      let dateA = a instanceof Date;
      let dateB = b instanceof Date;

      if (dateA != dateB) return false;
      if (dateA && dateB) return a.getTime() == b.getTime();

      let regexpA = a instanceof RegExp;
      let regexpB = b instanceof RegExp;

      if (regexpA != regexpB) return false;
      if (regexpA && regexpB) return a.toString() == b.toString();

      let keys = Object.keys(a);

      length = keys.length;

      if (length !== Object.keys(b).length) return false;

      for (i = length; i-- !== 0; ) if (!Object.prototype.hasOwnProperty.call(b, keys[i])) return false;

      for (i = length; i-- !== 0; ) {
          key = keys[i];
          if (!deepEquals(a[key], b[key])) return false;
      }

      return true;
  }

  return a !== a && b !== b;
}

function setLanguageByCode(code: string) {
  switch (code) {
    case 'en':
      return Language.English;
    case 'it':
      return Language.Italian;
    case 'de':
      return Language.German;
    case 'nl':
      return Language.Dutch;
    case 'fr':
      return Language.French;
    case 'pt':
      return Language.Portuguese;
    case 'es':
      return Language.Spanish;
    case 'cs':
      return Language.Czech;
    case 'pl':
      return Language.Polish;
    default:
      return Language.English
  }
}

function lineIsNullOrEmpty(line: string | null | undefined): boolean {
  return !line || line.trim() === '';
}

export {
  sleep,
  deepEquals,
  objectTypes,
  cloneObject,
  isValidEnum,
  getSlotClass,
  getRandomInt,
  getDeviceIcon,
  isDefaultScene,
  validLanguages,
  getUserInitials,
  defaultNewPlant,
  lineIsNullOrEmpty,
  getDeviceIconName,
  setLanguageByCode,
  resolveObjectPath,
  getObjectDescLabel,
  getPlantIconImages,
  getRoomIconFromType,
  getEpPlantIconImages,
  getObjectIconFromType,
  getPlantIconImageUrls,
  getThermo99Description,
  formattedTimeToSeconds,
  secondsToFormattedTime,
  getExtDeviceActiveLink,
  getExtDeviceIdentifier,
  getObjectLabelFromType,
  getIconFromProductType,
  getEpPlantIconImageUrls,
  defaultNewEntrancePlant,
  getUserInitialsForBubble,
  getExtDeviceIconFromType,
  getMxServerSettingsLabel,
  getPlantLastModifyByIcon,
  getPlantLastModifyByLabel,
  getDeviceIconFromSettings,
  getSceneObjectIconFromType,
  getPlantIconImageUrlsByType,
  getGeneralGroupIconFromType,
  parseShutterActuatorSettings,
  getCurrentThermoActuatorType,
  NAME_VALIDATION_DEBOUNCE_TIME,
  hasDeviceSettingsButtonVisible,
  hasDeviceCommandIconButtonVisible,
  getConfiguredChannelsOrModulesSettings,
  convertThermoRuleServiceTypeToThermoActuatorType,
};
